import React, { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Navigation, Power, PowerOff, Clock, CheckCircle, Truck, Loader2 } from "lucide-react";
import { logger } from "../utils/logger";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { id: "en_route", label: "En Route", icon: Truck, color: "bg-blue-500" },
  { id: "on_site", label: "On Site", icon: MapPin, color: "bg-emerald-500" },
  { id: "completed", label: "Completed", icon: CheckCircle, color: "bg-purple-500" },
];

export default function DecoratorTracker() {
  const [phone, setPhone] = useState("");
  const [verified, setVerified] = useState(false);
  const [tracking, setTracking] = useState(false);
  const [status, setStatus] = useState("en_route");
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError] = useState("");
  const [coords, setCoords] = useState(null);
  const [name, setName] = useState("");
  const intervalRef = useRef(null);
  const watchRef = useRef(null);

  const sendLocation = useCallback(async (lat, lng, accuracy, currentStatus) => {
    try {
      const res = await fetch(`${API_URL}/api/tracker/location`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ops_phone: phone, lat, lng, accuracy, status: currentStatus }),
      });
      if (res.ok) {
        setLastUpdate(new Date());
        setError("");
      } else {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || "Failed to update location");
      }
    } catch (err) {
      setError("Network error — will retry");
    }
  }, [phone]);

  const startTracking = () => {
    if (!navigator.geolocation) {
      setError("GPS not supported on this device");
      return;
    }
    setTracking(true);
    setError("");

    // Watch position continuously
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setCoords({ lat: latitude, lng: longitude, accuracy });
        sendLocation(latitude, longitude, accuracy, status);
      },
      (err) => {
        if (err.code === 1) setError("Please allow location access in your browser settings");
        else setError("GPS error — move to an open area");
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );

    // Also send periodic updates every 30s
    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          setCoords({ lat: latitude, lng: longitude, accuracy });
          sendLocation(latitude, longitude, accuracy, status);
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    }, 30000);
  };

  const stopTracking = async () => {
    if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    watchRef.current = null;
    intervalRef.current = null;
    setTracking(false);

    try {
      await fetch(`${API_URL}/api/tracker/stop`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ops_phone: phone }),
      });
    } catch (err) { logger.error('[Tracker] Stop error:', err.message); }
  };

  // Update status on server when changed
  useEffect(() => {
    if (tracking && coords) {
      sendLocation(coords.lat, coords.lng, coords.accuracy, status);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (watchRef.current !== null) navigator.geolocation.clearWatch(watchRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  // Verify phone screen
  if (!verified) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1E1B4B] to-[#0f0d2e] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] rounded-full flex items-center justify-center mx-auto mb-4">
            <Navigation size={28} className="text-white" />
          </div>
          <h1 className="text-xl font-bold text-[#1E1B4B] mb-1">DoozyGo Field Tracker</h1>
          <p className="text-sm text-[#94a3b8] mb-5">Enter your registered phone number to start</p>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="10-digit phone number"
            className="w-full px-4 py-3 rounded-xl border border-gray-200 text-center text-lg font-mono tracking-wider focus:border-[#E83E8C] outline-none mb-3"
            data-testid="tracker-phone-input"
          />
          <button
            onClick={() => {
              if (phone.length === 10) { setVerified(true); setName(phone); }
              else setError("Enter a valid 10-digit number");
            }}
            className="w-full py-3 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white font-bold rounded-xl"
            data-testid="tracker-verify-btn"
          >
            Start Tracking
          </button>
          {error && <p className="text-red-500 text-xs mt-2">{error}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1E1B4B] to-[#0f0d2e] p-4">
      <div className="max-w-sm mx-auto space-y-4">
        {/* Header */}
        <div className="text-center pt-4">
          <h1 className="text-white text-xl font-bold">DoozyGo Tracker</h1>
          <p className="text-[#94a3b8] text-sm">{phone}</p>
        </div>

        {/* Tracking Status Card */}
        <div className={`rounded-2xl p-6 text-center ${tracking ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-white/5 border border-white/10'}`}>
          {tracking ? (
            <>
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-3 relative">
                <MapPin size={28} className="text-emerald-400" />
                <span className="absolute top-0 right-0 w-4 h-4 bg-emerald-500 rounded-full animate-ping" />
                <span className="absolute top-0 right-0 w-4 h-4 bg-emerald-500 rounded-full" />
              </div>
              <p className="text-emerald-400 font-bold text-lg">Tracking Active</p>
              {coords && (
                <p className="text-white/50 text-xs mt-1 font-mono">
                  {coords.lat.toFixed(5)}, {coords.lng.toFixed(5)}
                  {coords.accuracy && <span> (~{Math.round(coords.accuracy)}m)</span>}
                </p>
              )}
              {lastUpdate && (
                <p className="text-white/40 text-xs mt-1 flex items-center justify-center gap-1">
                  <Clock size={10} /> Updated {lastUpdate.toLocaleTimeString()}
                </p>
              )}
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                <Navigation size={28} className="text-white/40" />
              </div>
              <p className="text-white/60 font-medium">Tracking Off</p>
              <p className="text-white/30 text-xs mt-1">Tap below to start sharing your location</p>
            </>
          )}
        </div>

        {/* Status Selector */}
        {tracking && (
          <div className="grid grid-cols-3 gap-2" data-testid="tracker-status-selector">
            {STATUS_OPTIONS.map(opt => {
              const Icon = opt.icon;
              const isActive = status === opt.id;
              return (
                <button
                  key={opt.id}
                  onClick={() => setStatus(opt.id)}
                  className={`p-3 rounded-xl text-center transition-all ${
                    isActive ? `${opt.color} text-white shadow-lg` : 'bg-white/10 text-white/60'
                  }`}
                  data-testid={`tracker-status-${opt.id}`}
                >
                  <Icon size={20} className="mx-auto mb-1" />
                  <span className="text-[11px] font-medium">{opt.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Start/Stop Button */}
        <button
          onClick={tracking ? stopTracking : startTracking}
          className={`w-full py-4 rounded-2xl font-bold text-lg flex items-center justify-center gap-3 transition-all ${
            tracking
              ? 'bg-red-500/20 text-red-400 border border-red-500/30'
              : 'bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white shadow-lg'
          }`}
          data-testid="tracker-toggle-btn"
        >
          {tracking ? <><PowerOff size={22} /> Stop Tracking</> : <><Power size={22} /> Start Tracking</>}
        </button>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-center">
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        {/* Info */}
        <div className="text-center pt-2">
          <p className="text-white/20 text-[10px]">Location shared with DoozyGo Ops team only</p>
          <p className="text-white/20 text-[10px]">Active hours: 8 AM - 10 PM</p>
        </div>
      </div>
    </div>
  );
}
